/* eslint-disable no-unreachable */
/* eslint-disable react-native/no-inline-styles */
/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import React, { useEffect, useMemo, useState } from 'react'
import cn from 'classnames'
import { Link } from 'react-router-dom'
import { connect } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import withInvite, {
  inviteStateProps,
  inviteDispatchProps,
} from '@sportninja/common/components/Invite'
import { t } from '@sportninja/common/i18n'
import {
  ROLE_TYPE_ID_TO_STRING,
  ROUTES,
} from '@sportninja/common/constants/app'
import { invitationType } from '@sportninja/common/constants/invitationType'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { queryKeys } from '@sportninja/common/config/queryKeys'

import LoadingSpinner from '../../components/LoadingSpinner'
import Helmet from '../../components/Helmet'
import ErrorScreen from '../../components/ErrorScreen'
import { FormButton } from '../../components/Form/css'
import Icon from '../../components/Icon'
import Waiver from './Waiver'
import { useUnderageInviteFlow } from './UnderageFlow/useUnderageInviteFlow'
import { canSelfRegister } from '@sportninja/common/features/invitation/helper/selfRegister'
import WelcomeStep from '../UnderageInvitation/components/WelcomeStep'
import PlayerStep from '../UnderageInvitation/components/PlayerStep'
import UnderAgeError from '../UnderageInvitation/components/UnderAgeError'
// import InvitePicker from './InvitePicker'
// import InviteCreateNew from './InviteCreateNew'

import './index.scss'
import './index-mobile.scss'
import { Image, Spin } from 'antd'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import {
  heading1,
  heading6,
  paragraphLarge,
  paragraphMedium,
  paragraphSmall,
  paragraphXSmall,
  weightBold,
  weightRegular,
  weightSemiBold,
} from 'src/components/css'
import { CheckBoxInput } from 'src/components/ts-components/CheckBoxInput'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import {
  getDestinationDisplayName,
  getInviteDisplayTitle,
  isGameInvitation,
  isGameOfficialInvitation,
  isVideographerInvitation,
} from './displayUtils'
import FamilyInvitation from './FamilyInvitation/FamilyInvitation'
import { isFamilyInvitation } from '@sportninja/common/features/profile/family/utils'
import TeamBudgetSlotInvitation from './TeamBudgetSlotInvitation/TeamBudgetSlotInvitation'
import { isTeamBudgetSlotInvitation } from '@sportninja/common/features/invitation/helper/teamBudgetSlot'
import { disableSubmitButton, getText } from './inviteWaiverUtils'

const entityType = {
  ORGANIZATION: 'Organization',
  TEAM: 'Team',
  COMPETITION: 'Competition',
  PLAYER: 'Player',
  GAME: 'Game',
}

export const getAnOrA = (type) => {
  if (
    typeof type === 'string' &&
    type.length > 0 &&
    ['a', 'e', 'i', 'o', 'u'].includes(type[0].toLowerCase())
  ) {
    return 'an'
  }
  return 'a'
}

const INVITE_ALREADY_ACCEPTED = 'This invitation has already been accepted.'

// The invite returns birth_date as a full ISO datetime
// (e.g. "2023-03-03T00:00:00+00:00"); DateOfBirthInput expects YYYY-MM-DD, so
// keep only the date portion. Returns '' for missing/short values.
const normalizeBirthDate = (value) =>
  typeof value === 'string' && value.length >= 10 ? value.slice(0, 10) : ''

const getInviteRoleDisplay = (invitee, roleName, invite) => {
  if (isVideographerInvitation(invite)) {
    return 'Videographer'
  }
  if (isGameOfficialInvitation(invite)) {
    return 'Game Official'
  }
  if (invitee.type !== 'Player' && roleName) {
    return roleName
  }
  return invitee.type
}

const getRouteDestination = (invitationTypeId, entityId) => {
  try {
    // Registration "bucket" invites resolve to a null destination on the
    // backend — fall back to the player's My Registrations view.
    if (!entityId) {
      return ROUTES.REGISTRATIONS.ROOT
    }
    const inviteType = invitationType.find(
      (item) => String(item.id) === String(invitationTypeId)
    )
    if (inviteType) {
      let screen
      switch (inviteType.destination) {
        case entityType.ORGANIZATION:
          screen = ROUTES.ORG_PAGES.replace(/:id/, entityId)
          break
        case entityType.TEAM:
          screen = ROUTES.TEAM_PAGES.replace(/:id/, entityId)
          break
        case entityType.COMPETITION:
          screen = ROUTES.SCHEDULE_PAGES.replace(/:id/, entityId)
          break
        case entityType.GAME:
          screen = ROUTES.GAME_PAGES.replace(/:id/, entityId)
          break
        default:
          break
      }
      return screen
    }
  } catch (e) {
    return ROUTES.HOME
  }
}

/**
 * Determines whether the submit button should be disabled based on the provided parameters.
 *
 * @param {Object} options - The options object.
 * @param {boolean} options.shouldDisplayWaiver - Indicates whether the waiver should be displayed.
 * @param {boolean} options.hasScrolled - Indicates whether the user has scrolled.
 * @param {boolean} options.loading - Indicates whether the data is being loaded.
 * @param {boolean} options.ask_position - Indicates whether the position should be asked.
 * @param {string|null} options.selectedPosition - The selected position.
 * @returns {boolean} - True if the submit button should be disabled, false otherwise.
 */

/**
 * Returns the invite type name based on the provided parameters.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.invitee - The invitee object.
 * @param {string} params.roleName - The role name.
 * @param {boolean} params.ask_position - Indicates if position is asked.
 * @param {boolean} params.isPlayer - Indicates if the invitee is a player.
 * @param {boolean} params.isTeamOfficial - Indicates if the invitee is a team official.
 * @param {Array} params.teamOfficialsTypes - The array of team official types.
 * @param {Array} params.playerTypes - The array of player types.
 * @param {string} params.selectedPosition - The selected position.
 * @returns {string} The invite type name.
 */
const getInviteTypeName = ({
  invitee,
  roleName,
  ask_position,
  isPlayer,
  isTeamOfficial,
  teamOfficialsTypes,
  playerTypes,
  selectedPosition,
}) => {
  if (ask_position) {
    if (isPlayer) {
      return playerTypes.find((type) => type.id === selectedPosition)?.name_full
    }
    if (isTeamOfficial) {
      return teamOfficialsTypes.find((type) => type.id === selectedPosition)
        ?.name_full
    }
  }
  if (invitee.type !== 'Player' && roleName) {
    return roleName
  } else {
    return invitee.type
  }
}

const Invite = ({
  accepted,
  acceptedNames,
  error,
  // existingUserIdx,
  invitee,
  inviterName,
  // isInviteForSelf,
  // isNewInvite,
  loading,
  managedUsers,
  message,
  onConfirm,
  // onCreate,
  roleTypeId,
  self,
  shouldDisplayWaiver,
  invite,
  acceptedFormat,
  waiverData,
  sports,
  sport_id,
  player_id,
  show_underage_workflow,
  family_created,
  ask_position,
  team_official_id,
  team_official_type_id,
  player_type_id,
  destination,
}) => {
  const invitationDestination =
    invitationType?.find((item) => String(item.id) === String(invite?.type?.id))
      ?.destination || null
  // const shouldDisplayWaiver = true
  // const ask_position = true
  const sport = sports[sport_id] || {}
  const playerTypes = useMemo(() => sport.players || [], [sport.players])
  const teamOfficialsTypes = useMemo(
    () => sport.teamOfficials || [],
    [sport.teamOfficials]
  )

  const isPlayer = !!player_id
  // Underage player invite: replace the accept area with the multi-step flow.
  // Only for player invites flagged underage AND whose family hasn't been
  // created yet. When family_created is true the parent/family already exists,
  // so we skip the underage steps and fall through to the regular flow.
  const isUnderagePlayer =
    Boolean(show_underage_workflow) && !family_created && isPlayer
  const isTeamOfficial = !!team_official_id
  const [hasScrolled, setHasScrolled] = useState(false)
  const [selectedPosition, setSelectedPosition] = useState(
    isPlayer ? playerTypes[0]?.id : teamOfficialsTypes[0]?.id || null
  )
  const [acceptedWaiver, setAcceptedWaiver] = useState(false)
  const image = getImageThumbnailId(destination) || null

  // Underage player invite: a multi-step flow (who → review / not-eligible →
  // accept) renders BEFORE the regular accept card. The who/review/not-eligible
  // steps replace the accept card; on the `accept` step the regular card below
  // renders verbatim, with Join carrying the captured player object. This keeps
  // the final step apple-to-apple with the standard flow (no duplicated markup).
  const underageFlow = useUnderageInviteFlow({
    playerId: player_id,
    onSubmit: (player) => onConfirm(self?.id, selectedPosition, player),
    // No birth_date on file → the invitee may be the player themselves, so
    // "yes, it's me" reviews the profile as Self instead of dead-ending.
    canSelfRegister: canSelfRegister(invite),
    initial: {
      nameFirst: invite?.name_first ?? '',
      nameLast: invite?.name_last ?? '',
      birthDate: normalizeBirthDate(invite?.birth_date),
    },
  })
  // True while the underage user is on an early step (before the accept card).
  const inUnderageSteps = isUnderagePlayer && underageFlow.step !== 'accept'

  // SN-7849: accepting marks the invitation's notices read and takes it off the
  // pending list, while the header bell and Home's invitations stay mounted or
  // cached. Refresh both, inactive queries included, so neither shows it still waiting.
  const queryClient = useQueryClient()
  useEffect(() => {
    if (!accepted) {
      return
    }
    queryClient.invalidateQueries({
      queryKey: queryKeys.notification.all,
      refetchType: 'all',
    })
    queryClient.invalidateQueries({
      queryKey: queryKeys.invitation.all,
      refetchType: 'all',
    })
  }, [accepted, queryClient])

  useEffect(() => {
    if (playerTypes && playerTypes.length > 0 && isPlayer) {
      setSelectedPosition(playerTypes[0]?.id)
    }
  }, [isPlayer, playerTypes])

  useEffect(() => {
    if (teamOfficialsTypes && teamOfficialsTypes.length > 0 && isTeamOfficial) {
      setSelectedPosition(teamOfficialsTypes[0]?.id)
    }
  }, [isTeamOfficial, teamOfficialsTypes])

  if (loading.page) {
    return <LoadingSpinner fullScreen />
  }

  if (!error && isFamilyInvitation(invite)) {
    return (
      <FamilyInvitation
        invite={invite}
        accepted={accepted}
        loading={loading}
        self={self}
        onConfirm={onConfirm}
        inviteeEmail={invitee?.email}
        inviteeName={
          [invitee?.nameFirst, invitee?.nameLast].filter(Boolean).join(' ') ||
          null
        }
      />
    )
  }

  // Jeff, Oct 1 — a Team Budget slot held for the invitee is taken by registering
  // for the team, never by accepting: its own page, with no accept action.
  if (!error && isTeamBudgetSlotInvitation(invite)) {
    return (
      <TeamBudgetSlotInvitation
        invite={invite}
        inviteeEmail={invitee?.email}
        inviteeName={
          [invitee?.nameFirst, invitee?.nameLast].filter(Boolean).join(' ') ||
          null
        }
      />
    )
  }

  const classes = cn('invite-section left flex flex-dir-col', {
    'remove-border': error || accepted || invitee.type !== 'player',
  })

  const removeTheFromName = inviterName.replace(/$the[/s]/i, '')
  const roleName = ROLE_TYPE_ID_TO_STRING?.[roleTypeId]
  const inviteName = getInviteTypeName({
    invitee,
    roleName,
    ask_position,
    isPlayer,
    isTeamOfficial,
    teamOfficialsTypes,
    playerTypes,
    selectedPosition,
  })

  return (
    <div
      css={css`
        width: 100%;
        height: 100%;
        display: flex;
        flex: 1;
        justify-content: center;
        align-items: center;
        padding: 40px 24px;
      `}
    >
      {error?.title === INVITE_ALREADY_ACCEPTED ? (
        <ErrorScreen
          title='INVITATION UNAVAILABLE'
          message='This invitation has already been accepted and is no longer available.'
          buttonText={
            isFamilyInvitation(error?.invite)
              ? 'GO TO THE PROFILE'
              : error?.invite?.destination?.id && invitationDestination
              ? `GO TO ${invitationDestination}`
              : !error?.invite?.destination?.id
              ? 'GO TO REGISTRATIONS'
              : 'GO TO PAGE'
          }
          onButtonPress={() => {
            window.location.href = isFamilyInvitation(error?.invite)
              ? ROUTES.PROFILE.ROOT
              : getRouteDestination(
                  error?.invite?.type?.id,
                  error?.invite?.destination?.id
                )
          }}
        />
      ) : error ? (
        <ErrorScreen
          title={error?.title || 'INVITATION UNAVAILABLE'}
          message={
            error?.message ||
            'This invitation could not be loaded. Please check the link and try again.'
          }
          onButtonPress={() => {
            window.location.href = ROUTES.HOME
          }}
        />
      ) : (
        <div
          css={css`
            display: flex;
            width: 600px;
            padding: 40px 24px;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            border-radius: 16px;
            border: 1px solid ${colors.SECONDARY_300};
            background: ${colors.SECONDARY_400};
            @media (max-width: 768px) {
              max-width: 100vw;
              min-width: 0;
            }
          `}
        >
          <>
            <Image
              width={80}
              height={80}
              css={css`
                border-radius: 8px;
              `}
              src={image}
              fallback='/images/generic_placeholders/placeholder_team.png'
              preview={false}
            />

            <p
              css={css`
                color: ${error ? colors.ERROR_LIGHT : colors.WHITE};
                text-align: center;
                text-transform: uppercase;
                ${heading1}
                font-family: ${isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif; !important'
                  : 'BarlowCondensed, Arial, Helvetica, sans-serif; !important'};
              `}
            >
              {error
                ? error.title || t('errors:somethingWentWrong')
                : accepted
                ? 'Ready To Go!'
                : getInviteDisplayTitle(inviterName, invite, t)}
            </p>
          </>
          {/* {!accepted && !error ? (
              <div
                css={css`
                  display: flex;
                  justify-content: flex-start;
                  align-items: center;
                  gap: 16px;
                  align-self: stretch;
                  flex: 1 0 0;
                `}
              >
              <Image
                width={80}
                height={80}
                css={css`
                  border-radius: 8px;
                `}
                src='error'
                fallback='/images/generic_placeholders/placeholder_team.png'
                preview={false}
              />

              <p
                css={css`
                  ${heading1}
                  ${weightSemiBold}
                  color: ${error ? colors.ERROR_LIGHT : colors.WHITE};
                  text-align: flex-start;
                  text-transform: uppercase;
                  font-family: ${isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif; !important'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif; !important'};
                `}
              >
                <span
                  css={css`
                    ${heading4}
                    ${weightMedium}
                    font-family: ${isCanlan
                      ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif; !important'
                      : 'BarlowCondensed, Arial, Helvetica, sans-serif; !important'};
                  `}
                >
                  {'Join the'}
                </span>
                <br />
                {removeTheFromName}
              </p>
            </div>
          ) : (
            <>
              <Image
                width={80}
                height={80}
                css={css`
                  border-radius: 8px;
                `}
                src='error'
                fallback='/images/generic_placeholders/placeholder_team.png'
                preview={false}
              />

              <p
                css={css`
                  color: ${error ? colors.ERROR_LIGHT : colors.WHITE};
                  text-align: center;
                  text-transform: uppercase;
                  ${heading1}
                  font-family: ${isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif; !important'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif; !important'};
                `}
              >
                {error
                  ? error.title || t('errors:somethingWentWrong')
                  : accepted
                  ? 'Ready To Go!'
                  : t('Web:joinTheX', {
                      x: inviterName,
                      interpolation: { escapeValue: false },
                    })}
              </p>
            </>
          )} */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: center;
                gap: 4px;
                align-self: stretch;
              `}
            >
              <p
                css={css`
                  color: ${colors.PRIMARY};
                  text-align: center;
                  ${paragraphLarge}
                  ${weightBold}
                align-self: stretch;
                `}
              >
                {acceptedNames.first || invitee.nameFirst}{' '}
                {acceptedNames.last || invitee.nameLast}{' '}
              </p>
              {invitee?.email ? (
                <p
                  css={css`
                    ${paragraphXSmall}
                    ${weightRegular}
                    color: ${colors.WHITE};
                    text-align: center;
                    align-self: center;
                  `}
                >
                  ({invitee?.email})
                </p>
              ) : null}
            </div>
            {!error && !accepted ? (
              <p
                css={css`
                  ${paragraphLarge}
                  ${weightRegular}
                color: ${colors.WHITE};
                  text-align: center;
                  align-self: center;
                `}
              >
                {t('Web:isInvitedToJoin')} {t('common:the')}{' '}
                <span
                  css={css`
                    ${paragraphLarge}
                    ${weightBold}
                  color: ${colors.PRIMARY};
                  `}
                >
                  {getDestinationDisplayName(removeTheFromName, invite)}
                </span>{' '}
                as a {getInviteRoleDisplay(invitee, roleName, invite)}
              </p>
            ) : null}
            {!error && accepted ? (
              <p
                css={css`
                  ${paragraphLarge}
                  ${weightRegular}
                color: ${colors.WHITE};
                  text-align: center;
                  align-self: stretch;
                `}
              >
                {isGameInvitation(invite) ? (
                  <>
                    Is Now A{' '}
                    <span
                      css={css`
                        ${paragraphLarge}
                        ${weightBold}
                      color: ${colors.PRIMARY};
                      `}
                    >
                      {isVideographerInvitation(invite)
                        ? 'Videographer'
                        : 'Game Official'}
                    </span>
                  </>
                ) : (
                  <>
                    is now a{' '}
                    <span
                      css={css`
                        ${paragraphLarge}
                        ${weightBold}
                      color: ${colors.PRIMARY};
                      `}
                    >
                      {inviteName}
                    </span>{' '}
                    of{' '}
                    <span
                      css={css`
                        ${paragraphLarge}
                        ${weightBold}
                      color: ${colors.PRIMARY};
                      `}
                    >
                      {getDestinationDisplayName(removeTheFromName, invite)}
                    </span>
                  </>
                )}
              </p>
            ) : null}
          </div>
          <div
            css={css`
              background: ${colors.HEADER};
              height: 1px;
              width: 100%;
            `}
          />
          {/* Underage flow — who / review / not-eligible steps render in place
              of the accept card. On the `accept` step they yield to the regular
              card below (rendered verbatim) so the final step is identical. */}
          {inUnderageSteps && underageFlow.step === 'who' ? (
            <WelcomeStep
              data={{
                ...invite,
                name_first: invite?.name_first,
                name_last: invite?.name_last,
                destination,
              }}
              radioOption={underageFlow.radioOption}
              relationship={underageFlow.relationship}
              setRadioOption={underageFlow.setRadioOption}
              setRelationship={underageFlow.setRelationship}
              fieldErrors={underageFlow.fieldErrors}
              customError={underageFlow.customError}
              onNext={() => underageFlow.fromWhoNext()}
              hideHeader
            />
          ) : null}
          {inUnderageSteps && underageFlow.step === 'notEligible' ? (
            <UnderAgeError
              onBack={() => underageFlow.fromNotEligibleBack()}
              hideHeader
            />
          ) : null}
          {inUnderageSteps && underageFlow.step === 'review' ? (
            <PlayerStep
              data={{
                ...invite,
                name_first: invite?.name_first,
                name_last: invite?.name_last,
                destination,
              }}
              stepState={[0]}
              fieldErrors={underageFlow.fieldErrors}
              customError={underageFlow.customError}
              playerName={underageFlow.nameFirst}
              playerLastName={underageFlow.nameLast}
              playerDOB={underageFlow.birthDate}
              playerHeightFT={underageFlow.heightFt}
              playerHeightIN={underageFlow.heightIn}
              playerWeight={underageFlow.weight}
              setPlayerName={underageFlow.setNameFirst}
              setPlayerLastName={underageFlow.setNameLast}
              setPlayerDOB={underageFlow.setBirthDate}
              setPlayerHeightFT={underageFlow.setHeightFt}
              setPlayerHeightIN={underageFlow.setHeightIn}
              setPlayerWeight={underageFlow.setWeight}
              validateForm={() => Promise.resolve(true)}
              onBack={() => underageFlow.fromReviewBack()}
              onNext={() => underageFlow.fromReviewNext()}
              hideHeader
            />
          ) : null}
          {!accepted && !error && !inUnderageSteps ? (
            <p
              css={css`
                ${paragraphMedium}
                ${weightRegular}
                    color: ${colors.WHITE};
                text-align: center;
                align-self: center;
              `}
            >
              {getText(shouldDisplayWaiver, ask_position)}
            </p>
          ) : null}
          {invitee.type === 'Player' &&
          isPlayer &&
          ask_position &&
          !accepted &&
          !inUnderageSteps ? (
            <>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 8px;
                  width: 100%;
                `}
              >
                {/* <p
                  css={css`
                    ${paragraphMedium}
                    ${weightRegular}
                    color: ${colors.WHITE};
                    text-align: center;
                    align-self: center;
                  `}
                >
                  To accept this invitation, please select your position:
                </p> */}
                <SelectInput
                  label={'Position:'}
                  disabled={false}
                  options={playerTypes.map((type) => ({
                    label: type.name_full,
                    value: type.id,
                  }))}
                  name={'position'}
                  value={selectedPosition}
                  showSearch={false}
                  onChange={(value) => {
                    setSelectedPosition(value)
                  }}
                  fieldErrors={[]}
                  customError={{}}
                  style={css`
                    background-color: ${colors.HEADER}!important;
                  `}
                />
              </div>
            </>
          ) : null}
          {isTeamOfficial && ask_position && !accepted ? (
            <>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 8px;
                  width: 100%;
                `}
              >
                <SelectInput
                  label={'Position:'}
                  disabled={false}
                  options={teamOfficialsTypes.map((type) => ({
                    label: type.name_full,
                    value: type.id,
                  }))}
                  name={'position'}
                  value={selectedPosition}
                  showSearch={false}
                  onChange={(value) => {
                    setSelectedPosition(value)
                  }}
                  fieldErrors={[]}
                  customError={{}}
                  style={css`
                    background-color: ${colors.HEADER}!important;
                  `}
                />
              </div>
            </>
          ) : null}
          {shouldDisplayWaiver && !accepted && !inUnderageSteps && (
            <Waiver
              hasScrolled={hasScrolled}
              setHasScrolled={setHasScrolled}
              waiverData={waiverData}
            />
          )}
          {shouldDisplayWaiver && !accepted && !inUnderageSteps ? (
            <div
              css={css`
                display: flex;
                flex-direction: row;
                gap: 8px;
                width: 100%;
                align-items: center;
                justify-content: start;
              `}
            >
              <CheckBoxInput
                customError={{}}
                fieldErrors={[]}
                disabled={false}
                label='I have read and agree to the waiver terms and conditions.'
                name='accepted_waiver'
                onChange={(value) => setAcceptedWaiver(value.target.checked)}
                value={acceptedWaiver}
                checkBoxStyle={css`
                  width: unset;
                  font-size: 14px;
                  line-height: 17px;
                `}
              />
            </div>
          ) : null}
          {!inUnderageSteps && (
            <div
              css={css`
                display: flex;
                align-items: flex-start;
                gap: 16px;
                align-self: stretch;
              `}
            >
              <button
                disabled={loading.accept}
                css={css`
                  display: flex;
                  padding: 8px 24px;
                  justify-content: center;
                  align-items: center;
                  gap: 8px;
                  flex: 1 0 0;
                  border-radius: 8px;
                  border: 2px solid ${colors.WHITE};
                  ${loading.accept ? 'opacity: 0.5;' : ''}
                `}
                onClick={() => {
                  window.location.href = ROUTES.HOME
                }}
              >
                <span
                  css={css`
                    color: ${colors.WHITE};
                    ${heading6}
                    ${weightSemiBold}
                  font-family: ${isCanlan
                      ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif; !important'
                      : 'BarlowCondensed, Arial, Helvetica, sans-serif; !important'};
                  `}
                >
                  {accepted ? 'GO HOME' : 'NOT NOW'}
                </span>
              </button>
              <button
                disabled={disableSubmitButton({
                  shouldDisplayWaiver,
                  hasScrolled,
                  loading: loading.accept,
                  ask_position,
                  selectedPosition,
                  acceptedWaiver,
                })}
                onClick={() => {
                  if (accepted) {
                    window.location.href = getRouteDestination(
                      invite?.type?.id,
                      invite?.destination?.id
                    )
                  } else if (isUnderagePlayer) {
                    // Underage accept step: build + send the captured player via
                    // the flow's submit (onConfirm(self.id, position, player)).
                    underageFlow.submit()
                  } else {
                    // onConfirm.bind(this, self.id, selectedPosition)
                    onConfirm(self.id, selectedPosition)
                  }
                }}
                css={css`
                  display: flex;
                  padding: 8px 24px;
                  justify-content: center;
                  align-items: center;
                  gap: 8px;
                  flex: 1 0 0;
                  align-self: stretch;
                  border-radius: 8px;
                  border: 2px solid
                    ${disableSubmitButton({
                      shouldDisplayWaiver,
                      hasScrolled,
                      loading: loading.accept,
                      ask_position,
                      selectedPosition,
                      acceptedWaiver,
                    })
                      ? colors.SECONDARY_300
                      : colors.PRIMARY};
                  background: ${disableSubmitButton({
                    shouldDisplayWaiver,
                    hasScrolled,
                    loading: loading.accept,
                    ask_position,
                    selectedPosition,
                    acceptedWaiver,
                  })
                    ? colors.SECONDARY_300
                    : colors.PRIMARY};
                  ${disableSubmitButton({
                    shouldDisplayWaiver,
                    hasScrolled,
                    loading: loading.accept,
                    ask_position,
                    selectedPosition,
                    acceptedWaiver,
                  })
                    ? 'opacity: 0.5;'
                    : ''}
                `}
              >
                {loading.accept ? (
                  <Spin
                    style={{
                      color: colors.WHITE,
                    }}
                  />
                ) : (
                  <span
                    css={css`
                      ${heading6}
                      ${weightSemiBold}
                    font-family: ${isCanlan
                        ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif; !important'
                        : 'BarlowCondensed, Arial, Helvetica, sans-serif; !important'};
                      /* Disabled swaps the fill to SECONDARY_300, so the
                         tenant-aware content only applies while enabled. */
                      color: ${disableSubmitButton({
                        shouldDisplayWaiver,
                        hasScrolled,
                        loading: loading.accept,
                        ask_position,
                        selectedPosition,
                        acceptedWaiver,
                      })
                        ? colors.WHITE
                        : PRIMARY_BUTTON_CONTENT.text};
                      text-transform: uppercase;
                      white-space: nowrap;
                      overflow: hidden;
                      text-overflow: ellipsis;
                    `}
                  >
                    {accepted
                      ? invite?.destination?.id
                        ? `GO TO ${invitationDestination}`
                        : 'GO TO REGISTRATIONS'
                      : 'JOIN'}
                  </span>
                )}
              </button>
            </div>
          )}
          {isUnderagePlayer &&
          underageFlow.step === 'accept' &&
          !accepted &&
          !error ? (
            <div
              css={css`
                display: flex;
                justify-content: center;
                width: 100%;
              `}
            >
              <button
                type='button'
                onClick={() => underageFlow.fromAcceptBack()}
                css={css`
                  ${paragraphSmall}
                  ${weightSemiBold}
                  background: transparent;
                  border: none;
                  padding: 4px 8px;
                  color: ${colors.NEUTRAL_50};
                  cursor: pointer;
                  &:hover {
                    color: ${colors.PRIMARY};
                  }
                `}
              >
                {'< Back'}
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )

  return (
    <div className='invite-v2'>
      <Helmet title='Invitation' />
      <div className={classes}>
        <h1 className='invite-title' style={{ whiteSpace: 'pre-line' }}>
          {error
            ? error.title || t('errors:somethingWentWrong')
            : t('Web:joinTheX', {
                x: inviterName,
                interpolation: { escapeValue: false },
              })}
          {error?.title === INVITE_ALREADY_ACCEPTED ? (
            <>
              <Link
                className={cn(
                  'sn-button invite-btn invite-finish-btn is--outline',
                  { 'is-canlan': isCanlan }
                )}
                to={getRouteDestination(
                  error?.invite?.type?.id,
                  error?.invite?.destination?.id
                )}
                title={t('util:capitalize', { text: 'Go to page' })}
              >
                {t('util:capitalize', { text: 'Go to page' })}
              </Link>
            </>
          ) : null}
        </h1>
        <h2 className='invite-sub-title' style={{ marginBottom: '12px' }}>
          {accepted ? (
            <>
              {t('Web:invitationAccepted')}
              <Link
                className={cn(
                  'sn-button invite-btn invite-finish-btn is--outline',
                  { 'is-canlan': isCanlan }
                )}
                to={getRouteDestination(
                  invite?.type?.id,
                  invite?.destination?.id
                )}
                title={t('util:capitalize', { text: t('routeNames:profile') })}
              >
                {t('util:capitalize', { text: 'Go to page' })}
              </Link>
            </>
          ) : error ? (
            error.message
          ) : (
            <>
              <span className='invite-highlight'>
                {acceptedNames.first || invitee.nameFirst}{' '}
                {acceptedNames.last || invitee.nameLast}{' '}
              </span>
              {invitee?.email && (
                <span className='invitee-email'>({invitee.email}) </span>
              )}
              {t('Web:isInvitedToJoin')} {t('common:the')}{' '}
              <span className='invite-highlight'>{removeTheFromName}</span>
              {invitee.type && (
                <>
                  {' '}
                  as {getAnOrA(invitee.type)}{' '}
                  <span className='invite-highlight'>
                    {invitee.type !== 'player' && roleName
                      ? roleName
                      : invitee.type}
                  </span>
                </>
              )}
              {message ? ':' : '.'}
              {message && (
                <div
                  style={{
                    borderRadius: 4,
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    padding: '12px',
                    marginTop: 16,
                  }}
                >
                  <Icon
                    name='comment-alt'
                    fontSize={16}
                    color='rgba(255, 255, 255, 0.5)'
                    style={{ marginRight: 12 }}
                  />
                  {message}
                </div>
              )}
              {managedUsers && (
                <div className='invite-mobile-scroller'>
                  <span className='icon'>
                    <i className='fas fa-angle-down' />
                  </span>
                </div>
              )}
            </>
          )}
        </h2>
        {!error && !accepted && (
          //   invitee.type === 'player' ? (
          //   <InvitePicker
          //     disabled={loading.accept}
          //     isNewInvite={isNewInvite}
          //     isInviteForSelf={isInviteForSelf}
          //     existingUserIdx={existingUserIdx}
          //     managedUsers={managedUsers}
          //     onConfirm={onConfirm}
          //     self={self}
          //   />
          // ) : (
          <div className='accept-button-wrap'>
            {shouldDisplayWaiver && (
              <Waiver
                hasScrolled={hasScrolled}
                setHasScrolled={setHasScrolled}
                waiverData={waiverData}
              />
            )}
            <FormButton
              busy={loading.accept}
              disabled={(shouldDisplayWaiver && !hasScrolled) || loading.accept}
              isSubmit
              className='accept-button'
              onClick={onConfirm.bind(this, self.id)}
              // title={
              //   !hasScrolled
              //     ? 'Read to the bottom of the waiver before joining.'
              //     : ''
              // }
            >
              {t('Web:join')}
            </FormButton>
          </div>
        )}
      </div>
      {/* {!error && !accepted && invitee.type === 'player' && (
        <div className='invite-section right flex flex-dir-col'>
          <InviteCreateNew
            disabled={loading.accept}
            invitee={invitee}
            onCreate={onCreate}
            self={self}
          />
        </div>
      )} */}
    </div>
  )
}

export default connect(
  inviteStateProps,
  inviteDispatchProps
)(withInvite(Invite))
